import React from 'react';
import PidDiagramShell, { PidPipe, PidPort } from './PidDiagramShell';
import { OlefinRoute } from '../../types';

/** 催化裂解（DCC）装置 P&ID 详细图：提升管反应 + 催化剂循环再生 + 主分馏 */
const media = {
  oil: { label: '蜡油 / 渣油 / 油浆', light: '#ef6c00', dark: '#ffb74d', width: 2.6 },
  gas: { label: '反应油气', light: '#2e7d32', dark: '#81c784', width: 2.6 },
  cat: { label: '催化剂（循环输送）', light: '#8e24aa', dark: '#ce93d8', width: 3.2, dash: '12 5' },
  air: { label: '主风（烧焦空气）', light: '#00838f', dark: '#4dd0e1', width: 2 },
};

const devices = [
  { id: 'v101', kind: 'hDrum' as const, x: 180, y: 189, tag: 'V-101', name: '原料油缓冲罐' },
  { id: 'r101', kind: 'reactor' as const, x: 380, y: 128, tag: 'R-101', name: '提升管反应器', note: '出口 530℃' },
  { id: 'd101', kind: 'vDrum' as const, x: 530, y: 161, tag: 'D-101', name: '沉降器' },
  { id: 'rg101', kind: 'regenerator' as const, x: 323, y: 430, tag: 'RG-101', name: '再生器' },
  { id: 't201', kind: 'column' as const, x: 720, y: 101, tag: 'T-201', name: '主分馏塔' },
  { id: 'c101', kind: 'compressor' as const, x: 900, y: 193, tag: 'C-101', name: '气压机' },
  { id: 't202', kind: 'column' as const, x: 1050, y: 101, tag: 'T-202', name: '吸收稳定塔' },
];

const ports: PidPort[] = [
  { id: 'p-feed', label: '蜡油/渣油', medium: 'oil', x: 0, y: 224, direction: 'in' as const },
  { id: 'p-air', label: '主风', medium: 'air', x: 100, y: 567, direction: 'in' as const },
  { id: 'p-lpg', label: '液化气（乙烯/丙烯/丁烯）', medium: 'gas', x: 1290, y: 52, direction: 'out' as const },
  { id: 'p-gasoline', label: '汽油', medium: 'oil', x: 1240, y: 224, direction: 'out' as const },
];

const pipes: PidPipe[] = [
  { id: 's01', from: 'p-feed', fromSide: 'right', to: 'v101', toSide: 'left', medium: 'oil' },
  { id: 's02', from: 'v101', fromSide: 'right', to: 'r101', toSide: 'left', medium: 'oil' },
  { id: 's04', from: 'r101', fromSide: 'right', to: 'd101', toSide: 'left', medium: 'gas' },
  { id: 's05', from: 'd101', fromSide: 'top', to: 't201', toSide: 'left', medium: 'gas' },
  { id: 's06', from: 't201', fromSide: 'right', to: 'c101', toSide: 'left', medium: 'gas' },
  { id: 's07', from: 'c101', fromSide: 'right', to: 't202', toSide: 'left', medium: 'gas' },
  { id: 's08', from: 't202', fromSide: 'top', to: 'p-lpg', toSide: 'left', medium: 'gas' },
  { id: 's09', from: 't201', fromSide: 'bottom', to: 'p-gasoline', toSide: 'left', medium: 'oil' },
  // 催化剂循环：沉降器→再生器（待生），再生器→提升管（再生）
  { id: 's10', from: 'd101', fromSide: 'bottom', to: 'rg101', toSide: 'right', medium: 'cat' },
  { id: 's11', from: 'rg101', fromSide: 'top', to: 'r101', toSide: 'bottom', medium: 'cat' },
  { id: 's12', from: 'p-air', fromSide: 'right', to: 'rg101', toSide: 'left', medium: 'air' },
];

const CatalyticCrackingDiagram: React.FC<{ route: OlefinRoute }> = ({ route }) => (
  <PidDiagramShell
    routeName={route.name}
    title="催化裂解装置详细流程"
    subtitle="提升管反应器（500~700℃）→ 沉降器 → 主分馏塔 → 气压机 → 吸收稳定塔；催化剂在再生器烧焦后循环回提升管（紫色虚线），油浆回炼（设备形状按 P&ID 惯例，管线按介质分色）"
    media={media}
    devices={devices}
    ports={ports}
    pipes={pipes}
    feedstock={route.feedstock}
    products={[...route.products, ...(route.byproducts ?? [])]}
  />
);

export default CatalyticCrackingDiagram;
